/**
 * Helper class for parsing DynamoDB partition keys (PK) and sort keys (SK)
 * Format: {class1}_{value1}#{class2}_{value2}#...
 * Examples:
 * - "corp_269351#office_05"
 * - "bathroom_2a29e030-472b-4132-a46a-a0cbe927470e"
 * - "corp_269351#office_B1#bathroom_b75a391f-841d-4f3a-b3ec-2545c66f9be4"
 * - "date_20251208"
 */
export class DynamoDBKeyHelper {
  /**
   * Extracts a value from a DynamoDB key by class name
   * @param key - The partition key or sort key string
   * @param className - The class name to search for (e.g., "corp", "office", "bathroom", "date")
   * @returns The value associated with the class name, or null if not found
   *
   * @example
   * const pk = "corp_269351#office_05";
   * const corpValue = DynamoDBKeyHelper.getValueByClass(pk, "corp"); // Returns "269351"
   * const officeValue = DynamoDBKeyHelper.getValueByClass(pk, "office"); // Returns "05"
   *
   * @example
   * const sk = "bathroom_2a29e030-472b-4132-a46a-a0cbe927470e";
   * const bathroomId = DynamoDBKeyHelper.getValueByClass(sk, "bathroom");
   * // Returns "2a29e030-472b-4132-a46a-a0cbe927470e"
   */
  public static getValueByClass(key: string, className: string): string | null {
    if (!key || !className) {
      return null;
    }

    // Split by '#' to get all segments
    const segments = key.split('#');

    // Search for the segment that starts with the class name
    for (const segment of segments) {
      const parts = segment.split('_');

      if (parts.length >= 2 && parts[0] === className) {
        // Join all parts after the first one (in case the value contains underscores)
        return parts.slice(1).join('_');
      }
    }

    return null;
  }

  /**
   * Extracts all class-value pairs from a DynamoDB key
   * @param key - The partition key or sort key string
   * @returns An object with class names as keys and their values
   *
   * @example
   * const pk = "corp_269351#office_05";
   * const parsed = DynamoDBKeyHelper.parseKey(pk);
   * // Returns { corp: "269351", office: "05" }
   *
   * @example
   * const sk = "corp_269351#office_B1#bathroom_b75a391f-841d-4f3a-b3ec-2545c66f9be4";
   * const parsed = DynamoDBKeyHelper.parseKey(sk);
   * // Returns { corp: "269351", office: "B1", bathroom: "b75a391f-841d-4f3a-b3ec-2545c66f9be4" }
   */
  public static parseKey(key: string): Record<string, string> {
    const result: Record<string, string> = {};

    if (!key) {
      return result;
    }

    // Split by '#' to get all segments
    const segments = key.split('#');

    for (const segment of segments) {
      const underscoreIndex = segment.indexOf('_');

      if (underscoreIndex > 0) {
        const className = segment.substring(0, underscoreIndex);
        const value = segment.substring(underscoreIndex + 1);
        result[className] = value;
      }
    }

    return result;
  }

  /**
   * Builds a DynamoDB key from class-value pairs
   * @param pairs - An object with class names as keys and their values
   * @returns A formatted key string
   *
   * @example
   * const key = DynamoDBKeyHelper.buildKey({ corp: "269351", office: "05" });
   * // Returns "corp_269351#office_05"
   *
   * @example
   * const key = DynamoDBKeyHelper.buildKey({
   *   corp: "269351",
   *   office: "B1",
   *   bathroom: "b75a391f-841d-4f3a-b3ec-2545c66f9be4"
   * });
   * // Returns "corp_269351#office_B1#bathroom_b75a391f-841d-4f3a-b3ec-2545c66f9be4"
   */
  public static buildKey(pairs: Record<string, string>): string {
    return Object.entries(pairs)
      .map(([className, value]) => `${className}_${value}`)
      .join('#');
  }
}
